import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, TextInput } from "../../../../../components";
import { validateEmail } from "../../../../../assets/js/validation";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../../../../../config/firebaseConfig";
import envConfig from "../../../../../config/envConfig";
import { GAEventTrack } from "../../../../../utils/hooks/GA_events";
import { toast } from "react-toastify";

const ForgotPasswordForm = ({ onBackToLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Cooldown countdown timer for resending reset email
  useEffect(() => {
    let timer;
    if (emailSent && resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [emailSent, resendCooldown]);

  const handleSendResetEmail = async (isResend = false) => {
    setError(null);
    const cleanEmail = email.trim().toLowerCase();

    if (!cleanEmail) {
      setError({ email: "Email is required." });
      return;
    }
    if (!validateEmail(cleanEmail)) {
      setError({ email: "Please enter a valid email address." });
      return;
    }

    if (isResend) {
      setResendLoading(true);
    } else {
      setLoading(true);
    }

    try {
      const baseUrl = (envConfig?.appUrl || window.location.origin).replace(/\/+$/, "");
      const continueAction = {
        url: `${baseUrl}/login/email`,
        handleCodeInApp: true,
      };

      await sendPasswordResetEmail(auth, cleanEmail, continueAction).catch(async () => {
        // Fallback without action settings if custom domain redirect is not whitelisted
        await sendPasswordResetEmail(auth, cleanEmail);
      });

      try {
        GAEventTrack({
          event: isResend ? "pass_reset_email_resent" : "pass_reset_email_sent",
          data: { email: cleanEmail },
        });
      } catch (_) {
        // Ignore analytics errors
      }

      setEmailSent(true);
      setResendCooldown(60);
      toast.success(
        isResend
          ? "A new password reset link has been sent to your email."
          : "Password reset link sent! Please check your inbox."
      );
    } catch (err) {
      console.error("Password reset error:", err);
      const errMsg = err?.message || "";

      if (err?.code === "auth/user-not-found" || errMsg.toLowerCase().includes("user not found")) {
        setError({ email: "No account found with this email address. Please check your email or sign up." });
      } else if (err?.code === "auth/invalid-email" || errMsg.toLowerCase().includes("invalid-email")) {
        setError({ email: "Please enter a valid email address." });
      } else if (err?.code === "auth/too-many-requests" || errMsg.toLowerCase().includes("too-many-requests")) {
        setError({ other: "Too many reset attempts. Please wait a short while before trying again." });
      } else {
        setError({ other: err?.message || "Failed to send password reset email. Please try again." });
      }
    } finally {
      setLoading(false);
      setResendLoading(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSendResetEmail(false);
  };

  const navigateBack = () => {
    if (onBackToLogin) {
      onBackToLogin();
    } else {
      navigate("/login/email" + (location.search || ""));
    }
  };

  if (emailSent) {
    return (
      <div className="w-full h-full flex flex-col justify-start">
        {/* Back Button */}
        <button
          type="button"
          onClick={() => setEmailSent(false)}
          className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-gray-800 hover:text-black mb-2 transition-colors w-fit focus:outline-none cursor-pointer"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          <span>Back</span>
        </button>

        {/* Heading */}
        <h3 className="text-xl sm:text-2xl font-extrabold text-[#111827] tracking-tight leading-tight mb-1">
          Reset Link Sent
        </h3>

        {/* Visual Envelope */}
        <div className="flex justify-center my-3">
          <div className="w-14 h-14 rounded-full bg-[#E0F2FE] flex items-center justify-center text-[#009DFF] shadow-sm">
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
              />
            </svg>
          </div>
        </div>

        {/* Subheading */}
        <p className="text-xs text-gray-500 font-medium mb-4 text-center leading-relaxed">
          We've sent a password reset link to <span className="font-bold text-gray-800">{email}</span>. Please
          check your inbox and click the link to create a new password.
        </p>

        {error?.other && (
          <div className="text-[#E5383B] text-xs font-semibold mb-2.5 text-center">{error.other}</div>
        )}

        {/* Action Button */}
        <div className="w-full mt-1">
          <Button
            version="secondary"
            text="Back to Sign In"
            type="button"
            handleClick={navigateBack}
            onClick={navigateBack}
            width="100%"
            className="w-full justify-center font-extrabold"
          />
        </div>

        {/* Resend Section */}
        <div className="flex flex-col items-center justify-center mt-5 text-center w-full select-none">
          <p className="text-xs text-[#64748B] font-medium">
            Didn't receive the email?{" "}
            {resendCooldown === 0 ? (
              <button
                type="button"
                onClick={() => handleSendResetEmail(true)}
                disabled={resendLoading}
                className="text-[#009DFF] hover:underline font-bold focus:outline-none cursor-pointer"
              >
                Resend
              </button>
            ) : (
              <span className="text-[#94A3B8] font-bold">Resend</span>
            )}
          </p>

          {resendCooldown > 0 ? (
            <div className="mt-2 inline-flex items-center px-4 py-1.5 rounded-full bg-[#F1F5F9] text-[#475569] text-xs font-bold shadow-sm">
              Resend in 00:{String(resendCooldown).padStart(2, "0")}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => handleSendResetEmail(true)}
              disabled={resendLoading}
              className="mt-2 inline-flex items-center px-4 py-1.5 rounded-full bg-[#009DFF]/10 hover:bg-[#009DFF]/20 text-[#009DFF] text-xs font-bold transition-colors cursor-pointer"
            >
              {resendLoading ? "Sending..." : "Resend Link"}
            </button>
          )}
        </div>

        {/* Change Email Link */}
        <button
          type="button"
          onClick={() => {
            setEmailSent(false);
            setError(null);
          }}
          className="mt-4 text-xs font-bold text-gray-400 hover:text-gray-700 hover:underline block mx-auto transition duration-200 cursor-pointer"
        >
          ← Change email address
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleFormSubmit} className="w-full h-full flex flex-col justify-start" noValidate>
      {/* Back Button */}
      <button
        type="button"
        onClick={navigateBack}
        className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-gray-800 hover:text-black mb-2 transition-colors w-fit focus:outline-none cursor-pointer"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
        </svg>
        <span>Back to Sign In</span>
      </button>

      {/* Heading & Subheading */}
      <h3 className="text-xl sm:text-2xl font-extrabold text-[#111827] tracking-tight leading-tight mb-0.5">
        Forgot Password?
      </h3>
      <p className="text-xs text-gray-500 font-medium mb-4">
        Enter your registered email address and we'll send you a link to reset your password.
      </p>

      {/* Email Input */}
      <div className="form-group flex flex-col mb-3">
        <label htmlFor="email" className="text-xs sm:text-sm font-bold text-gray-700 mb-1">
          Email Address <span className="text-[#E5383B]">*</span>
        </label>
        <TextInput
          type="email"
          placeholderText="alex@example.com"
          inputName="email"
          inputValue={email}
          isRequired={true}
          inputClassName="!h-[44px] !rounded-xl !bg-white !border-gray-200 focus:!border-[#6366F1] !text-sm"
          leftIcon={
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
              />
            </svg>
          }
          handleChange={(e) => {
            setEmail(e.target.value);
            if (error?.email) {
              setError((prev) => (prev ? { ...prev, email: null } : null));
            }
          }}
        />
        {error?.email && <div className="text-xs text-[#f00] mt-1 pl-1">{error.email}</div>}
      </div>

      {error?.other && <div className="text-[#E5383B] text-xs font-semibold mb-2.5">{error.other}</div>}

      {/* Submit Button */}
      <div className="w-full mt-2">
        <Button
          version="secondary"
          text="Send Reset Link"
          children={
            <svg className="w-4 h-4 ml-1 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          }
          type="submit"
          loading={loading}
          width="100%"
          className="w-full justify-center font-extrabold"
        />
      </div>

      {/* Footer Link */}
      <div className="text-center mt-4">
        <p className="text-xs text-gray-500 font-medium">
          Remember your password?{" "}
          <button
            type="button"
            onClick={navigateBack}
            className="text-[#009DFF] hover:underline font-bold focus:outline-none cursor-pointer"
          >
            Sign in here
          </button>
        </p>
      </div>
    </form>
  );
};

export default ForgotPasswordForm;
